import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  Inject,
  Input,
  Optional,
  Output,
  TemplateRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { TranslateService } from '@ngx-translate/core';
import {
  ModalTemplateComponent,
  PencilModalSizeEnum,
} from '../modal-template/modal-template.component';
import { PencilButtonComponent } from '../../pencil-button/pencil-button.component';
import { IconTypes } from '../../pencil-icon/pencil-icon.component';

export enum ModalStandardTemplateEnum {
  UNSAVED_CHANGES = 'unsaved_changes',
}

/** Optional img graphic above the description. */
export interface ConfirmationModalBodyImg {
  url: string;
  width?: string;
  height?: string;
  alt?: string;
}

export interface ModalData {
  type?: ModalStandardTemplateEnum;
  title?: string;
  description?: string;
  size?: PencilModalSizeEnum;
  showBodyImg?: boolean;
  bodyImg?: ConfirmationModalBodyImg;
  descriptionGap?: boolean;
  cancelButtonConfig?: {
    icon: string;
    iconType: IconTypes;
    label: string;
  };
  confirmButtonConfig?: {
    icon: string;
    iconType: IconTypes;
    label: string;
    danger?: boolean;
    outlined?: boolean;
  };
  hasCloseButton?: boolean;
  // If true, the modal will NOT auto-close when confirm is clicked.
  // The consumer should manually close it via the reference when appropriate.
  preventAutoCloseOnConfirm?: boolean;
}

@Component({
  selector: 'app-confirmation-modal',
  standalone: true,
  imports: [CommonModule, ModalTemplateComponent, PencilButtonComponent, MatDialogModule],
  templateUrl: './confirmation-modal.component.html',
  styleUrls: ['./confirmation-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationModalComponent {
  @Input() title?: string;
  @Input() description?: string;
  @Input() cancelButtonConfig?: {
    icon: string;
    iconType: IconTypes;
    label: string;
  };
  @Input() confirmButtonConfig?: {
    icon: string;
    iconType: IconTypes;
    label: string;
    danger?: boolean;
    outlined?: boolean;
  };
  @Input() hasCloseButton = false;
  @Input() preventAutoCloseOnConfirm = false;
  @Input() size: PencilModalSizeEnum = PencilModalSizeEnum.SMALL;
  @Input() showBodyImg = false;
  @Input() bodyImg?: ConfirmationModalBodyImg;
  @Input() descriptionGap = true;

  @Output() cancelButtonClicked = new EventEmitter<Event>();
  @Output() confirmButtonClicked = new EventEmitter<Event>();
  @Output() confirmButtonHovered = new EventEmitter<boolean>();
  @Output() closeButtonClicked = new EventEmitter<Event>();

  @ContentChild('customCancelButton') customCancelButton?: TemplateRef<any>;

  DEFAULT_MODAL_TEMPLATES: Record<ModalStandardTemplateEnum, ModalData> = {
    [ModalStandardTemplateEnum.UNSAVED_CHANGES]: {
      title: this.translateService.instant('Unsaved changes'),
      description: this.translateService.instant(
        'Are you sure you want to continue? Any unsaved changes will be lost.',
      ),
      confirmButtonConfig: {
        icon: 'check',
        iconType: IconTypes.matIcon,
        label: this.translateService.instant('Confirm'),
        danger: true,
        outlined: false,
      },
      cancelButtonConfig: {
        icon: 'close',
        iconType: IconTypes.matIcon,
        label: this.translateService.instant('Cancel'),
      },
      hasCloseButton: false,
    },
  };

  pencilModalSizeEnum = PencilModalSizeEnum;

  constructor(
    @Optional() public dialogRef: MatDialogRef<ConfirmationModalComponent>,
    @Optional() public bottomSheetRef: MatBottomSheetRef<ConfirmationModalComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public matDialogData: ModalData,
    @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) public matBottomSheetData: ModalData,
    private translateService: TranslateService,
  ) {
    const inputDialogData = this.renderedAsMatDialog ? this.matDialogData : this.matBottomSheetData;

    // Only process dialog data if it exists
    if (inputDialogData) {
      const renderDialogData = inputDialogData.type
        ? this.DEFAULT_MODAL_TEMPLATES[inputDialogData.type]
        : inputDialogData;

      this.title = renderDialogData.title;
      this.description = renderDialogData.description;
      this.cancelButtonConfig = renderDialogData.cancelButtonConfig;
      this.confirmButtonConfig = renderDialogData.confirmButtonConfig;
      this.hasCloseButton = renderDialogData.hasCloseButton ?? false;
      this.preventAutoCloseOnConfirm = renderDialogData.preventAutoCloseOnConfirm ?? false;
      this.size = renderDialogData.size ?? PencilModalSizeEnum.SMALL;
      this.showBodyImg = renderDialogData.showBodyImg ?? false;
      this.bodyImg = renderDialogData.bodyImg;
      this.descriptionGap = renderDialogData.descriptionGap ?? true;
    }

    if (this.renderedAsMatDialog) {
      this.dialogRef._containerInstance._config.autoFocus = false; // Set autofocus false in bulk
    }

    if (this.renderedAsMatBottomSheet) {
      // Workaround to get access to underlying bottomSheetElement
      const ref = (this.bottomSheetRef.containerInstance as any)._elementRef.nativeElement;
      if (ref) {
        ref.querySelector('input, button, [autofocus]')?.removeAttribute('autofocus'); // Set autofocus to false for MatBottomSheets
      }
    }
  }

  handleCancelClick(event: Event): void {
    if (this.renderedAsMatDialog) {
      this.dialogRef.close('cancel');
      return;
    }

    if (this.renderedAsMatBottomSheet) {
      this.bottomSheetRef.dismiss('cancel');
      return;
    }

    this.cancelButtonClicked.emit(event);
  }

  handleConfirmClick(event: Event): void {
    if (this.renderedAsMatDialog) {
      if (this.preventAutoCloseOnConfirm) {
        this.confirmButtonClicked.emit(event);
        return;
      }
      this.dialogRef.close('confirm');
      return;
    }

    if (this.renderedAsMatBottomSheet) {
      this.bottomSheetRef.dismiss('confirm');
      return;
    }

    this.confirmButtonClicked.emit(event);
  }

  handleConfirmButtonHovered(buttonHovered: boolean): void {
    this.confirmButtonHovered.emit(buttonHovered);
  }

  handleCloseButtonClick(event: Event): void {
    if (this.renderedAsMatDialog) {
      this.dialogRef.close('close');
      return;
    }

    if (this.renderedAsMatBottomSheet) {
      this.bottomSheetRef.dismiss('close');
      return;
    }

    this.closeButtonClicked.emit(event);
  }

  get renderedAsMatDialog(): boolean {
    return !!this.matDialogData && Object.keys(this.matDialogData).length > 0;
  }

  get renderedAsMatBottomSheet(): boolean {
    return !!this.matBottomSheetData && Object.keys(this.matBottomSheetData).length > 0;
  }
}
